/**
 * Utilidades para crear patrones a mano (sin plantilla).
 */

export const CREATION_MODE = {
  TEMPLATE: 'template',
  MANUAL: 'manual',
};

export const MANUAL_PATTERN_TYPE = {
  FIXED: 'fixed',
  ROTATING: 'rotating',
};

const FIXED_WEEKDAYS = [
  { dayOfWeek: 1, label: 'Lunes' },
  { dayOfWeek: 2, label: 'Martes' },
  { dayOfWeek: 3, label: 'Miércoles' },
  { dayOfWeek: 4, label: 'Jueves' },
  { dayOfWeek: 5, label: 'Viernes' },
  { dayOfWeek: 6, label: 'Sábado' },
  { dayOfWeek: 0, label: 'Domingo' },
];

function emptyManualRow({ dayOfWeek = null, daySequence = 0, label = '' } = {}) {
  return {
    key: dayOfWeek != null ? `dow-${dayOfWeek}` : `seq-${daySequence}`,
    dayOfWeek,
    daySequence,
    label,
    isWorkingDay: false,
    startTime: '08:00',
    endTime: '17:00',
    useBreak: false,
    breakMinutes: 30,
    shiftCode: 'Trabajo',
  };
}

/** Filas por defecto: L–V laborables 08:00–17:00 (solo creación manual) */
export function defaultFixedManualRows() {
  return FIXED_WEEKDAYS.map(({ dayOfWeek, label }) => ({
    ...emptyManualRow({ dayOfWeek, label }),
    isWorkingDay: dayOfWeek >= 1 && dayOfWeek <= 5,
  }));
}

/** Semana L–D en descanso; base para cargar un patrón fijo existente al editar */
function fixedWeekRowsAllRest() {
  return FIXED_WEEKDAYS.map(({ dayOfWeek, label }) =>
    emptyManualRow({ dayOfWeek, label })
  );
}

/** Filas de ciclo rotativo (todas descanso por defecto) */
export function defaultRotatingManualRows(cycleDays) {
  const n = Math.max(1, Math.min(60, Number(cycleDays) || 7));
  return Array.from({ length: n }, (_, daySequence) =>
    emptyManualRow({ daySequence, label: `Día ${daySequence}` })
  );
}

/** Ajusta el número de filas al cambiar cycle_days conservando datos previos */
export function resizeRotatingManualRows(prevRows, cycleDays) {
  const n = Math.max(1, Math.min(60, Number(cycleDays) || 7));
  return Array.from({ length: n }, (_, daySequence) => {
    const existing = prevRows.find((r) => r.daySequence === daySequence);
    if (existing) return { ...existing, label: `Día ${daySequence}` };
    return emptyManualRow({ daySequence, label: `Día ${daySequence}` });
  });
}

function parseHours(start, end, useBreak, breakMinutes) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  if (useBreak && breakMinutes) {
    mins -= Number(breakMinutes) || 0;
  }
  return Math.max(0, Math.round((mins / 60) * 100) / 100);
}

/** Convierte filas del formulario al payload de days-add */
export function buildPatternDaysFromManualRows(manualType, rows) {
  const working = rows.filter((r) => r.isWorkingDay);
  return working.map((row, index) => {
    const totalHours = parseHours(row.startTime, row.endTime, row.useBreak, row.breakMinutes);
    const day = {
      day_sequence: manualType === MANUAL_PATTERN_TYPE.FIXED ? index : row.daySequence,
      day_of_week: manualType === MANUAL_PATTERN_TYPE.FIXED ? row.dayOfWeek : null,
      shift_code: (row.shiftCode || 'Trabajo').trim(),
      is_working_day: true,
      start_time: row.startTime,
      end_time: row.endTime,
      break_minutes: row.useBreak ? Number(row.breakMinutes) || 0 : null,
      total_hours: totalHours > 0 ? totalHours : null,
    };
    return day;
  });
}

export function validateManualPatternRows(manualType, rows, { cycleDays, cycleStart } = {}) {
  const working = rows.filter((r) => r.isWorkingDay);
  if (working.length === 0) {
    return 'Marca al menos un día laborable.';
  }
  for (const row of working) {
    if (!row.startTime || !row.endTime) {
      return `Indica hora de inicio y fin en ${row.label}.`;
    }
    if (!row.shiftCode?.trim()) {
      return `Indica un código de turno en ${row.label}.`;
    }
    if (row.useBreak && (!row.breakMinutes || Number(row.breakMinutes) <= 0)) {
      return `Indica la duración estimada de la pausa (minutos) en ${row.label}.`;
    }
  }
  if (manualType === MANUAL_PATTERN_TYPE.ROTATING) {
    const n = Number(cycleDays);
    if (!n || n < 1 || n > 60) {
      return 'El ciclo rotativo debe tener entre 1 y 60 días.';
    }
    if (!cycleStart) {
      return 'Indica la fecha de inicio del ciclo (día 0).';
    }
  }
  return null;
}

export function describeManualPatternDays(manualType, rows, cycleDays) {
  const working = rows.filter((r) => r.isWorkingDay);
  if (manualType === MANUAL_PATTERN_TYPE.FIXED) {
    const labels = working.map((r) => r.label);
    return `${working.length} días laborables (${labels.join(', ') || 'ninguno'})`;
  }
  const seqs = working.map((r) => r.daySequence).sort((a, b) => a - b);
  return `${working.length} días laborables en ciclo de ${cycleDays} (secuencias ${seqs.join(', ') || '—'})`;
}

/** Convierte pattern_days del API a filas del formulario manual (edición: solo días del patrón) */
export function patternDaysToManualRows(pattern, patternDays) {
  const isRotating = Boolean(pattern?.is_rotating);
  const manualType = isRotating ? MANUAL_PATTERN_TYPE.ROTATING : MANUAL_PATTERN_TYPE.FIXED;
  const cycleDays = pattern?.cycle_days || 7;

  // Edición: estructura de filas vacía (descanso); se activan solo los días del API
  let rows = isRotating
    ? defaultRotatingManualRows(cycleDays)
    : fixedWeekRowsAllRest();

  const days =
    patternDays ??
    pattern?.pattern_days ??
    pattern?.PatternDays ??
    [];
  const dayList = Array.isArray(days) ? days : [];
  for (const day of dayList) {
    if (!day.is_working_day) continue;
    const useBreak = Boolean(day.break_minutes && Number(day.break_minutes) > 0);
    const patch = {
      isWorkingDay: true,
      startTime: day.start_time || '08:00',
      endTime: day.end_time || '17:00',
      shiftCode: day.shift_code || 'Trabajo',
      useBreak,
      breakMinutes: useBreak ? Number(day.break_minutes) : 30,
    };
    if (isRotating) {
      const idx = rows.findIndex((r) => r.daySequence === day.day_sequence);
      if (idx >= 0) rows[idx] = { ...rows[idx], ...patch };
    } else if (day.day_of_week != null) {
      const idx = rows.findIndex((r) => r.dayOfWeek === day.day_of_week);
      if (idx >= 0) rows[idx] = { ...rows[idx], ...patch };
    }
  }

  return { manualType, manualCycleDays: cycleDays, manualRows: rows };
}
